import type { layout } from "@app/domain";
import type { Snapshot } from "@app/protocol";
import { hearts, turnMsFor } from "@app/rules";
import { useState } from "react";
import { useTableSounds } from "../audio/useTableSounds";
import { Slot, Stage } from "../canvas/Stage";
import { Deal } from "../components/Deal";
import { Hand } from "../components/Hand";
import { useLeaveConfirm } from "../components/LeaveConfirm";
import { Watching } from "../components/Overlays";
import type { TurnClock } from "../components/Pod";
import { usePassChoice } from "../components/passChoice";
import { SeatPods } from "../components/SeatPods";
import { seatSlots } from "../components/seatOrder";
import { TableInfo } from "../components/TableInfo";
import { Band, isAuto, Overlay } from "../components/TableParts";
import { TopBar, useBack } from "../components/TopBar";
import { Trick } from "../components/Trick";
import { TurnPanel } from "../components/TurnPanel";
import { useDeal } from "../components/useDeal";
import { useT } from "../i18n/context";
import { gameOf } from "../variants";
import { type AnyView, isTrickGame } from "../variants/types";
import { targetLabel } from "./lobbyText";

import { tableSeatLook } from "./tableText";
import { toastFor } from "./tableToast";
import { useToastClock } from "./useToastClock";

/** The whole of the running clock; Hearts' pass is longer than a card. */
function turnBudget(view: AnyView, tempo: Snapshot["table"]["settings"]["tempo"]): number {
  if (view.phase === "passing") return hearts.passMsFor(tempo);
  // Baloot's doubling and King's choice of game are decisions: they run on the bidding clock.
  if (view.phase === "doubling" || view.phase === "choosing") return turnMsFor(tempo, "bidding");
  return turnMsFor(tempo, view.phase);
}

/**
 * When the acting seat's time runs out on this client's clock. `turnMs` is what
 * was left when the snapshot arrived; the pod draws the clock itself every frame.
 */
function useTurnClock(snapshot: Snapshot, view: AnyView): TurnClock | null {
  const [mark, setMark] = useState(() => ({ snapshot, at: Date.now() }));
  if (mark.snapshot !== snapshot) setMark({ snapshot, at: Date.now() });
  if (snapshot.turnMs === null) return null;
  const tempo = snapshot.table.settings.tempo;
  return { endsAt: mark.at + snapshot.turnMs, budget: turnBudget(view, tempo) };
}

/** The middle of the table: Baloot's turned card while the call goes round, else the trick. */
function Middle({
  view,
  slots,
  rect,
  card,
  names,
}: {
  view: AnyView;
  slots: ReturnType<typeof seatSlots>;
  rect: layout.Rect;
  card: { w: number; h: number };
  names: readonly (string | null)[];
}) {
  // A game may put something of its own in the middle (Baloot's turned card); else the trick.
  const own = gameOf(view).Middle?.({ view, card, slots, names }) ?? null;
  if (own !== null || !isTrickGame(view)) return own;
  return <Trick view={view} slots={slots} rect={rect} card={card} />;
}

/** The game: seats, trick, hand, panels, toast, scores. See docs/ekranlar/masa.md. */
export function Table({
  snapshot,
  measure,
  lastError,
}: {
  snapshot: Snapshot;
  measure: layout.Measure;
  lastError: string | null;
}) {
  const { t, err } = useT();
  const { table, you } = snapshot;
  const view = snapshot.view as AnyView;
  const canvas = measure.canvas;
  const el = canvas.elements;
  const slots = seatSlots(you.seat);
  const clock = useTurnClock(snapshot, view);
  const seated = you.seat !== null;
  const pass = usePassChoice(view, you.seat);
  const yourTurn = seated && (view.turn === you.seat || pass !== null);
  const finished = table.status === "finished";
  const playing = table.status === "playing";
  const leaving = useLeaveConfirm(useBack("table", seated), seated && playing);
  const back = leaving.back;
  const auto = isAuto(table.seats, you.seat);
  const dense = measure.state !== "wide";
  const deal = useDeal(view, seated, canvas);
  useToastClock();
  useTableSounds({ snapshot, yourTurn, deal: deal.run, clock });
  return (
    <Stage
      measure={measure}
      scene={table.scene}
      overlay={
        <Overlay
          finished={finished}
          seated={seated}
          view={view}
          seats={table.seats}
          target={table.settings.goal === null ? null : targetLabel(t, table.settings)}
          dialog={leaving.dialog}
        />
      }
    >
      <Slot rect={el.topBar} className="z-20">
        <TopBar
          state={measure.state}
          back={back}
          watchers={table.spectators.length}
          variant={table.settings.variant}
          seated={you.seat !== null}
          info={<TableInfo view={view} seats={table.seats} yourSeat={you.seat} />}
        />
      </Slot>
      <SeatPods
        variant={table.settings.variant}
        status={table.status}
        gifts={table.gifts}
        seats={table.seats}
        slots={slots}
        canvas={canvas}
        look={(seat, index) => tableSeatLook(view, seat, index, you.seat, clock, t)}
      />
      <Slot rect={el.trick}>
        <Middle
          view={view}
          slots={slots}
          rect={el.trick}
          card={canvas.trickCard}
          names={table.seats.map((s) => s?.name ?? null)}
        />
      </Slot>
      <Slot rect={el.band} className="z-20">
        <Band
          auto={auto}
          dense={dense}
          toast={toastFor(view, table.seats, you.seat, lastError, t, err)}
        />
      </Slot>
      {/* Clip, not hide: a hidden overflow scrolls to a focused card and lifts the whole hand. */}
      <Slot
        className="overflow-clip"
        rect={el.hand}
        label={seated ? t("table.hand.title") : t("table.watching")}
      >
        {seated ? (
          <Hand
            view={view}
            yourTurn={yourTurn}
            canvas={canvas}
            active={view.phase === "playing"}
            hidden={deal.run?.hidden ?? null}
            pass={pass}
          />
        ) : (
          <Watching dense={dense} />
        )}
      </Slot>
      {deal.run ? (
        <Deal
          key={deal.run.id}
          run={deal.run}
          canvas={canvas}
          hand={view.yourHand}
          onDone={deal.done}
        />
      ) : null}
      {/* Your panel waits for the deal: nothing to bid on while the cards are in the air. */}
      {yourTurn && !finished && deal.run === null ? (
        <Slot rect={el.actions} className="z-20">
          <TurnPanel
            view={view}
            names={table.seats.map((s) => s?.name ?? null)}
            measure={measure}
            pass={pass}
          />
        </Slot>
      ) : null}
    </Stage>
  );
}
